<template>
  <div class="flex h-full min-h-0 flex-col bg-white border-l border-black/5">
    <!-- ── Toolbar ───────────────────────────────────────────────────── -->
    <div
      class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-200 bg-white shrink-0 flex-wrap"
    >
      <!-- Page navigation -->
      <div class="flex items-center gap-1">
        <button
          @click="goPrev"
          :disabled="currentPage <= 1"
          class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
          title="Previous page"
        >
          <Icon icon="mdi:chevron-left" class="text-base" />
        </button>
        <div class="flex items-center gap-1">
          <input
            type="number"
            :min="1"
            :max="totalPages || 1"
            v-model.number="pageInput"
            class="w-12 text-center text-xs border border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            @keydown.enter="goToPage(pageInput)"
            @blur="pageInput = currentPage"
          />
          <span class="text-xs text-slate-400 whitespace-nowrap"
            >of {{ totalPages || "…" }}</span
          >
        </div>
        <button
          @click="goNext"
          :disabled="!totalPages || currentPage >= totalPages"
          class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
          title="Next page"
        >
          <Icon icon="mdi:chevron-right" class="text-base" />
        </button>
      </div>

      <div class="w-px h-4 bg-slate-200 mx-0.5 shrink-0" />

      <!-- Zoom -->
      <div class="flex items-center gap-1">
        <button
          @click="zoomOut"
          :disabled="scaleIdx <= 0"
          class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
          title="Zoom out"
        >
          <Icon icon="mdi:minus" class="text-base" />
        </button>
        <span class="text-xs text-slate-600 w-10 text-center select-none">{{
          zoomLabel
        }}</span>
        <button
          @click="zoomIn"
          :disabled="scaleIdx >= ZOOM_STEPS.length - 1"
          class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
          title="Zoom in"
        >
          <Icon icon="mdi:plus" class="text-base" />
        </button>
      </div>

      <div class="w-px h-4 bg-slate-200 mx-0.5 shrink-0" />

      <!-- Spread / layout mode -->
      <div class="flex items-center gap-0.5 rounded-md bg-slate-100 p-0.5">
        <button
          @click="setSpread('none')"
          class="p-1 rounded transition-all"
          :class="
            spreadMode === 'none'
              ? 'bg-white shadow-sm text-slate-700'
              : 'text-slate-400 hover:text-slate-600'
          "
          title="Single page"
        >
          <Icon icon="mdi:view-sequential" class="text-base" />
        </button>
        <button
          @click="setSpread('odd')"
          class="p-1 rounded transition-all"
          :class="
            spreadMode === 'odd'
              ? 'bg-white shadow-sm text-slate-700'
              : 'text-slate-400 hover:text-slate-600'
          "
          title="Two-page spread"
        >
          <Icon icon="mdi:book-open-outline" class="text-base" />
        </button>
      </div>

      <div class="w-px h-4 bg-slate-200 mx-0.5 shrink-0" />

      <!-- Search -->
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <div class="relative min-w-0 flex-1 max-w-xs">
          <Icon
            icon="mdi:magnify"
            class="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"
          />
          <input
            ref="searchEl"
            v-model="searchQuery"
            placeholder="Search… (Ctrl+F)"
            class="w-full pl-7 pr-3 py-1 text-xs border rounded-md bg-white transition-colors focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
            :class="
              searchQuery && findState === 1
                ? 'border-red-300 focus:ring-red-300 focus:border-red-300'
                : 'border-slate-200'
            "
            @input="onSearchInput"
            @keydown.enter.prevent="goNextMatch"
            @keydown.shift.enter.prevent="goPrevMatch"
            @keydown.esc.prevent="clearSearch"
          />
        </div>
        <template v-if="searchQuery">
          <span
            class="text-xs whitespace-nowrap shrink-0"
            :class="findState === 1 ? 'text-red-400' : 'text-slate-500'"
          >
            {{
              findState === 1
                ? "No results"
                : matchTotal
                ? `${matchCurrent} / ${matchTotal}`
                : "…"
            }}
          </span>
          <button
            @click="goPrevMatch"
            :disabled="!matchTotal"
            class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
            title="Previous match (Shift+Enter)"
          >
            <Icon icon="mdi:chevron-up" class="text-sm" />
          </button>
          <button
            @click="goNextMatch"
            :disabled="!matchTotal"
            class="p-1 rounded hover:bg-slate-100 disabled:opacity-30 text-slate-600 transition-colors"
            title="Next match (Enter)"
          >
            <Icon icon="mdi:chevron-down" class="text-sm" />
          </button>
          <button
            @click="clearSearch"
            class="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            title="Clear (Esc)"
          >
            <Icon icon="mdi:close" class="text-sm" />
          </button>
        </template>
      </div>
    </div>

    <!-- ── Viewer ────────────────────────────────────────────────────── -->
    <!-- outer: fills remaining flex space and establishes a positioned context -->
    <div class="flex-1 relative bg-slate-400">
      <!-- PDFViewer requires the container to be position:absolute -->
      <div ref="containerRef" class="absolute inset-0 overflow-auto">
        <div
          v-if="loading"
          class="absolute inset-0 flex flex-col items-center gap-3 justify-center text-slate-500 z-10 bg-slate-400"
        >
          <Icon icon="mdi:loading" class="text-3xl animate-spin" />
          <span class="text-sm">Loading PDF…</span>
        </div>

        <div
          v-else-if="loadError"
          class="absolute inset-0 flex items-center justify-center z-10 bg-slate-400"
        >
          <div
            class="px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600 max-w-md text-center"
          >
            {{ loadError }}
          </div>
        </div>

        <div ref="viewerRef" class="pdfViewer" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import * as pdfjsLib from "pdfjs-dist";
import {
  EventBus,
  PDFViewer,
  PDFLinkService,
  PDFFindController,
  SpreadMode,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";
import apiClient from "@/services/api";
import { useApplicationStore } from "@/stores/ApplicationStore";
import { storeToRefs } from "pinia";
import { usePathsStore } from "@/stores/CartStore";
import { makeBaseItems } from "@/utils/makeBaseItems";
import { onDownload } from "@/helpers/download";

/* ── Worker ──────────────────────────────────────────────────────── */
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).href;

/* ── Props / store ───────────────────────────────────────────────── */
const props = defineProps({
  name: { type: String, default: "" },
  file: { type: String, default: "" },
});

const app = useApplicationStore();
const { current_selected } = storeToRefs(app);
const pathsStore = usePathsStore();

const baseItems = makeBaseItems({
  onDownload,
  apiClient: () => apiClient,
  selection: () => current_selected.value,
});
const items = computed(() => {
  const extra = pathsStore.hasName(props.name)
    ? []
    : [
        {
          type: "item",
          label: "Add to Download cart",
          icon: "mdi:file-document-add",
          action: () =>
            pathsStore.add({
              path: current_selected.value.path,
              name: props.name,
            }),
        },
      ];
  return [...baseItems.value, ...extra];
});

/* ── DOM refs ────────────────────────────────────────────────────── */
const containerRef = ref(null);
const viewerRef = ref(null);
const searchEl = ref(null);

/* ── pdfjs instances ─────────────────────────────────────────────── */
let eventBus = null;
let pdfLinkService = null;
let pdfFindController = null;
let pdfViewerInstance = null;
let pdfDocument = null;

/* ── State ───────────────────────────────────────────────────────── */
const totalPages = ref(0);
const currentPage = ref(1);
const pageInput = ref(1);
const loading = ref(false);
const loadError = ref("");
const spreadMode = ref("none");

const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0];
const scaleIdx = ref(ZOOM_STEPS.indexOf(1.0));
const scale = computed(() => ZOOM_STEPS[scaleIdx.value]);
const zoomLabel = computed(() => `${Math.round(scale.value * 100)}%`);
function zoomIn() {
  if (scaleIdx.value < ZOOM_STEPS.length - 1) scaleIdx.value++;
}
function zoomOut() {
  if (scaleIdx.value > 0) scaleIdx.value--;
}

const searchQuery = ref("");
const matchCurrent = ref(0);
const matchTotal = ref(0);
const findState = ref(null); // 0=FOUND, 1=NOT_FOUND, 2=WRAPPED, 3=PENDING

/* ── Lifecycle ───────────────────────────────────────────────────── */
onMounted(() => {
  initViewer();
  loadPdf();
  document.addEventListener("keydown", onGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onGlobalKeydown);
  pdfDocument?.destroy();
});

/* ── Init PDFViewer ──────────────────────────────────────────────── */
function initViewer() {
  eventBus = new EventBus();

  pdfLinkService = new PDFLinkService({ eventBus });

  pdfFindController = new PDFFindController({
    linkService: pdfLinkService,
    eventBus,
    updateMatchesCountOnProgress: false,
  });

  pdfViewerInstance = new PDFViewer({
    container: containerRef.value,
    viewer: viewerRef.value,
    eventBus,
    linkService: pdfLinkService,
    findController: pdfFindController,
    textLayerMode: 1, // ENABLE — text selection + search
    annotationMode: 1, // ENABLE — clickable links
  });

  pdfLinkService.setViewer(pdfViewerInstance);

  eventBus.on("pagesinit", () => {
    totalPages.value = pdfViewerInstance.pagesCount;
    pdfViewerInstance.currentScale = scale.value;
  });

  eventBus.on("pagechanging", ({ pageNumber }) => {
    currentPage.value = pageNumber;
    pageInput.value = pageNumber;
  });

  eventBus.on("updatefindmatchescount", ({ matchesCount }) => {
    matchCurrent.value = matchesCount.current || 0;
    matchTotal.value = matchesCount.total || 0;
  });

  eventBus.on("updatefindcontrolstate", ({ state, matchesCount }) => {
    findState.value = state;
    if (matchesCount) {
      matchCurrent.value = matchesCount.current || 0;
      matchTotal.value = matchesCount.total || 0;
    }
  });
}

/* ── Load PDF ────────────────────────────────────────────────────── */
async function loadPdf() {
  loading.value = true;
  loadError.value = "";
  totalPages.value = 0;
  currentPage.value = 1;
  pdfDocument?.destroy();
  pdfDocument = null;

  try {
    const res = await apiClient.getPdf(props.file, {
      responseType: "arraybuffer",
    });
    pdfDocument = await pdfjsLib.getDocument({ data: new Uint8Array(res.data) })
      .promise;
    pdfViewerInstance.setDocument(pdfDocument);
    pdfLinkService.setDocument(pdfDocument, null);
    loading.value = false;
  } catch (e) {
    loadError.value = `Failed to load PDF: ${e?.message || e}`;
    loading.value = false;
  }
}

watch(
  () => props.file,
  () => {
    if (props.file) loadPdf();
  },
);

/* ── Zoom ────────────────────────────────────────────────────────── */
watch(scale, (s) => {
  if (pdfViewerInstance) pdfViewerInstance.currentScale = s;
});

/* ── Spread mode ─────────────────────────────────────────────────── */
function setSpread(mode) {
  spreadMode.value = mode;
  if (pdfViewerInstance) {
    pdfViewerInstance.spreadMode =
      mode === "odd" ? SpreadMode.ODD : SpreadMode.NONE;
  }
}

/* ── Page navigation ─────────────────────────────────────────────── */
watch(currentPage, (n) => {
  pageInput.value = n;
});

function goToPage(n) {
  const clamped = Math.max(1, Math.min(n, totalPages.value));
  if (pdfViewerInstance) pdfViewerInstance.currentPageNumber = clamped;
}
function goPrev() {
  if (currentPage.value > 1) goToPage(currentPage.value - 1);
}
function goNext() {
  if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
}

/* ── Search ──────────────────────────────────────────────────────── */
let searchDebounce = null;

function dispatchFind(findPrevious, type) {
  eventBus?.dispatch("find", {
    type,
    query: searchQuery.value,
    highlightAll: true,
    caseSensitive: false,
    entireWord: false,
    findPrevious,
  });
}

function onSearchInput() {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    if (searchQuery.value.trim()) {
      matchCurrent.value = 0;
      matchTotal.value = 0;
      findState.value = null;
      dispatchFind(false, "");
    } else {
      clearSearch();
    }
  }, 280);
}

function goNextMatch() {
  if (searchQuery.value) dispatchFind(false, "again");
}
function goPrevMatch() {
  if (searchQuery.value) dispatchFind(true, "again");
}

function clearSearch() {
  searchQuery.value = "";
  matchCurrent.value = 0;
  matchTotal.value = 0;
  findState.value = null;
  eventBus?.dispatch("findbarclose");
}

/* ── Ctrl+F ──────────────────────────────────────────────────────── */
function onGlobalKeydown(e) {
  if ((e.ctrlKey || e.metaKey) && e.key === "f") {
    e.preventDefault();
    searchEl.value?.focus();
    searchEl.value?.select();
  }
}
</script>

<style scoped>
/* Page cards */
:deep(.pdfViewer .page) {
  margin: 0 auto 1.5rem;
  border: none !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.12);
}

/* Search highlight colours (override pdfjs defaults) */
:deep(.textLayer .highlight) {
  background-color: rgba(253, 224, 71, 0.5) !important;
  border-radius: 2px;
}
:deep(.textLayer .highlight.selected) {
  background-color: rgba(251, 146, 60, 0.6) !important;
  outline: 1px solid rgba(251, 146, 60, 0.9) !important;
}
</style>
